import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, X, RefreshCw, TriangleAlert, CheckCircle2, CircleDashed } from 'lucide-react';
import { financeService } from '../../services/financeService';
import { useAppContext } from '../../context/AppContext';

// =============================================================================
// Finance › Budget Control  (WIRED — this page is real)
//
// Who has signed off, and who has changed their mind since.
//
// THREE STATES, NOT TWO. Every submission tracker ever built shows submitted or
// not submitted, and every one of them hides the state that actually costs you
// the month:
//
//   SUBMITTED        signed off, and nothing has moved since
//   SUBMITTED, THEN  signed off, then somebody edited the numbers. It reads as
//   EDITED           DONE on every other screen in the product, and it is the
//                    commonest reason a consolidated total moves after everyone
//                    has stopped looking at it.
//   OUTSTANDING      not submitted
//
// The middle one is why this page exists. Two-state heat maps are decoration.
//
// THE MAP IS THIS VERSION'S BUDGET SETUP. The axes are not the module list and
// the site list — they are what Budget Setup › GL Budget Lines says is being
// budgeted. A client with no depots has no Distribution column at all, rather
// than a column that can never go green; a plant nobody budgets is off the map
// rather than permanently red. A map with squares on it that nobody will ever
// fill in teaches people to ignore the map, which costs you the one thing it
// was for. Anything genuinely missing is a GL Budget Lines problem, and the
// page says so instead of quietly showing a gap.
//
// CROSS-SITE. The site selector in the top bar does not apply here — the whole
// point is looking across every site at once.
// =============================================================================

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors';

const SHORT = {
  MOD_1_OPEX: 'Opex',
  MOD_2_SUPPLY: 'Purchase',
  MOD_3_MAKE: 'Mfg',
  MOD_4_MOVE: 'Distribution',
  MOD_5_CAPEX: 'Capital',
  MOD_6_PROFIT: 'Revenue',
  MOD_8_PEOPLE: 'People',
  MOD_9_PLAN: 'Planning',
};

const fmtWhen = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};
const fmtFull = (ts) => (ts ? new Date(ts).toLocaleString() : '');

const EMPTY = { modules: [], sites: [], cells: [], scope: {} };
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export default function SubmissionStatus() {
  const navigate = useNavigate();
  const { selectedVersion } = useAppContext();

  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await financeService.getSubmissions();
      setData(res.data || EMPTY);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load submission status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedVersion) load(); }, [load, selectedVersion]);

  const cellAt = useMemo(() => {
    const m = new Map();
    (data.cells || []).forEach((c) => m.set(`${c.site_code}|${c.module_code}`, c));
    return m;
  }, [data.cells]);

  // Only budgeted squares count. A denominator padded with cells nobody was
  // ever going to fill in makes 100% unreachable, and a completion figure that
  // cannot reach 100% is not a completion figure.
  const stats = useMemo(() => {
    const cells = (data.cells || []).filter((c) => c.in_scope);
    return {
      total: cells.length,
      done: cells.filter((c) => c.submitted && !c.stale).length,
      stale: cells.filter((c) => c.stale).length,
      open: cells.filter((c) => !c.submitted).length,
    };
  }, [data.cells]);

  const pct = stats.total ? Math.round((stats.done / stats.total) * 100) : 0;
  const scope = data.scope || {};
  // An empty map has two causes and they need different sentences: nobody has
  // set the version up, or the setup points at sites that no longer exist.
  const nothingConfigured = !loading && !error
    && ((data.modules || []).length === 0 || (data.sites || []).length === 0);
  const strandedLines = nothingConfigured && scope.gl_line_count > 0;

  // Per-module tallies for the footer row — the column-wise read of the same
  // grid, which is the one a module owner cares about.
  const byModule = useMemo(() => {
    const out = new Map();
    (data.modules || []).forEach((m) => {
      const col = (data.cells || []).filter((c) => c.module_code === m.module_code && c.in_scope);
      out.set(m.module_code, {
        total: col.length,
        done: col.filter((c) => c.submitted && !c.stale).length,
      });
    });
    return out;
  }, [data.modules, data.cells]);

  if (!selectedVersion) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">Select a budget version to see submission status.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-4">
        <button className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                onClick={() => navigate('/finance')}>
          <ArrowLeft className="w-3.5 h-3.5" /> Finance
        </button>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Budget Control</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              What <strong>{selectedVersion}</strong> is set up to budget — who has signed off, and who has changed the
              numbers since they did. Across all sites; the site selector does not apply here.
            </p>
            {scope.gl_line_count > 0 && (
              <p className="mt-1 text-xs text-slate-400">
                {plural(scope.modules_on_map, 'module', 'modules')} × {plural(scope.sites_on_map, 'site', 'sites')},
                from {scope.gl_line_count.toLocaleString()} GL budget line
                {scope.gl_line_count === 1 ? '' : 's'} in Budget Setup.
                {scope.modules_available > scope.modules_on_map && (
                  <> {scope.modules_available - scope.modules_on_map} module
                    {scope.modules_available - scope.modules_on_map === 1 ? ' is' : 's are'} off the map —
                    nothing in this version is budgeted against {scope.modules_available - scope.modules_on_map === 1 ? 'it' : 'them'}.</>
                )}
              </p>
            )}
          </div>
          <button className={btn} onClick={load} disabled={loading}>
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {error && (
          <div className="mt-3 flex items-start justify-between gap-2 px-3 py-2 rounded-lg border border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10">
            <p className="m-0 text-xs text-red-700 dark:text-red-300">{error}</p>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}

        {/* Nothing budgeted means nothing to count — "0 of 0, 100%" is worse
            than silence. */}
        {stats.total > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Stat label="Signed off" value={`${stats.done} of ${stats.total}`} sub={`${pct}%`} tone="good" />
            <Stat label="Submitted, then edited" value={stats.stale} tone={stats.stale ? 'warn' : 'flat'}
                  sub={stats.stale ? 'sign-off no longer covers the numbers' : 'nothing changed after sign-off'} />
            <Stat label="Outstanding" value={stats.open} tone={stats.open ? 'bad' : 'good'} />
          </div>
        )}

        {stats.stale > 0 && (
          <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10">
            <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="m-0 text-xs text-amber-800 dark:text-amber-300">
              <strong>{stats.stale}</strong> submission{stats.stale === 1 ? ' has' : 's have'} been edited since sign-off.
              Everywhere else in Mantle they still read as submitted — this is the only screen that says otherwise.
              Either the owner re-submits, or the consolidated number is not the one they approved.
            </p>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6 overflow-auto">
        {loading ? (
          <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : nothingConfigured ? (
          // Not an error and not an empty network — nobody has said what this
          // version budgets yet. Send them to the one screen that fixes it.
          <div className="max-w-xl mx-auto mt-10 px-4 py-6 rounded-xl border border-dashed border-slate-300 dark:border-slate-600 text-center">
            <p className="m-0 text-sm font-semibold text-slate-700 dark:text-slate-200">
              {strandedLines
                ? `${selectedVersion} has GL budget lines, but none of them land on an active site.`
                : `Nothing is budgeted in ${selectedVersion} yet.`}
            </p>
            <p className="mt-1.5 mb-0 text-xs text-slate-500 dark:text-slate-400">
              {strandedLines ? (
                <>
                  All {scope.gl_line_count} line{scope.gl_line_count === 1 ? '' : 's'} point at sites that have since
                  been deactivated in master data, so there is nobody left to submit them. Either reactivate the sites
                  or re-point the lines in <strong>Budget Setup › GL Budget Lines</strong>.
                </>
              ) : (
                <>
                  This map is built from the GL budget lines set up for the version — which accounts, at which sites,
                  owned by which module. Until there is at least one line there is nothing to sign off and nothing to
                  chase. Add them in <strong>Budget Setup › GL Budget Lines</strong>.
                </>
              )}
            </p>
            <button className={`${btn} mt-4`} onClick={() => navigate('/budget-setup')}>Open Budget Setup</button>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-slate-100 dark:bg-slate-700/60 text-left">
                  <th className="px-3 py-2 font-bold uppercase text-[10px] tracking-wider text-slate-500 dark:text-slate-400 sticky left-0 bg-slate-100 dark:bg-slate-700/60 z-10">
                    Site
                  </th>
                  {data.modules.map((m) => (
                    <th key={m.module_code}
                        title={`${SHORT[m.module_code] || m.module_name} — ${plural(m.line_count || 0, 'GL budget line', 'GL budget lines')} in ${selectedVersion}`}
                        className="px-2 py-2 font-bold uppercase text-[10px] tracking-wider text-slate-500 dark:text-slate-400 text-center whitespace-nowrap">
                      {SHORT[m.module_code] || m.module_name}
                    </th>
                  ))}
                  <th className="px-3 py-2 font-bold uppercase text-[10px] tracking-wider text-slate-500 dark:text-slate-400 text-right">Done</th>
                </tr>
              </thead>
              <tbody>
                {data.sites.map((s, idx) => {
                  const row = data.modules.map((m) => cellAt.get(`${s.site_code}|${m.module_code}`));
                  // Denominator is what this site is actually on the hook for,
                  // which is rarely every column on the map.
                  const scoped = row.filter((c) => c?.in_scope);
                  const done = scoped.filter((c) => c.submitted && !c.stale).length;
                  return (
                    <tr key={s.site_code} className={`border-t border-slate-100 dark:border-slate-700/60 ${idx % 2 ? 'bg-slate-50/50 dark:bg-slate-800/40' : ''}`}>
                      <td className="px-3 py-1.5 whitespace-nowrap sticky left-0 bg-inherit">
                        <span className="font-semibold text-slate-700 dark:text-slate-200">{s.site_name}</span>
                        <span className="block font-mono text-[10px] text-slate-400">{s.site_code}</span>
                      </td>
                      {row.map((c, i) => <HeatCell key={data.modules[i].module_code} cell={c} />)}
                      <td className={`px-3 py-1.5 text-right font-mono whitespace-nowrap ${
                        done === scoped.length ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'}`}>
                        {done}/{scoped.length}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Column-wise read of the same grid — the one a module owner
                  cares about, and the quickest way to see which function is
                  holding up the consolidation. */}
              <tfoot>
                <tr className="border-t-2 border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/70">
                  <td className="px-3 py-1.5 font-bold uppercase text-[10px] tracking-wider text-slate-500 dark:text-slate-400 sticky left-0 bg-inherit">
                    Signed off
                  </td>
                  {data.modules.map((m) => {
                    const t = byModule.get(m.module_code) || { done: 0, total: 0 };
                    return (
                      <td key={m.module_code} className={`px-2 py-1.5 text-center font-mono text-[11px] ${
                        t.total && t.done === t.total ? 'text-emerald-600 dark:text-emerald-400 font-bold' : 'text-slate-500 dark:text-slate-400'}`}>
                        {t.done}/{t.total}
                      </td>
                    );
                  })}
                  <td className={`px-3 py-1.5 text-right font-mono text-[11px] font-bold ${
                    stats.total && stats.done === stats.total ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-600 dark:text-slate-300'}`}>
                    {stats.done}/{stats.total}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-4 text-[11px] text-slate-500 dark:text-slate-400">
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Signed off
          </span>
          <span className="inline-flex items-center gap-1.5">
            <TriangleAlert className="w-3.5 h-3.5 text-amber-600" /> Submitted, then edited
          </span>
          <span className="inline-flex items-center gap-1.5">
            <CircleDashed className="w-3.5 h-3.5 text-slate-400" /> Outstanding
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3.5 text-center text-slate-300 dark:text-slate-600">·</span> Not budgeted
          </span>
          <span className="text-slate-400">Hover any square for who signed it off, when, and what puts it on the map.</span>
        </div>

        <p className="m-0 mt-3 text-[11px] text-slate-400 max-w-4xl">
          <strong className="text-slate-500 dark:text-slate-400">What is on the map.</strong> Every square comes from
          Budget Setup &rsaquo; GL Budget Lines for {selectedVersion}: a module appears when it owns at least one line
          (a line with no module set counts as Opex, as it does on the entry screens), and a site appears when it
          carries one. Lines saved against <em>all sites</em> open that module at every site, which is why a site with
          no lines of its own can still show a square — the tooltip says which. Deactivated sites are left off even
          if old lines still point at them. Nothing here is hard-coded per client: turn a module on or off by adding
          or removing its lines.
        </p>

        <p className="m-0 mt-2 text-[11px] text-slate-400 max-w-4xl">
          &ldquo;Edited since&rdquo; compares the sign-off timestamp against the last write in that module&rsquo;s own tables.
          Coverage is deliberately partial for now — Opex, Distribution, Revenue and Manufacturing are checked;
          Purchase, Capital, People and Planning show sign-off only until their table map is added to
          <code className="mx-1">MODULE_TABLES</code> in the finance controller. A module with no map never reads as stale,
          which is the safe direction to be wrong in: it under-warns rather than crying wolf.
        </p>
      </div>
    </div>
  );
}

// One square. Colour carries the state; the tooltip carries the detail, because
// a grid of eight modules across ten sites has no room for names and dates.
//
// A cell with no GL budget lines behind it gets NO square at all — not a grey
// one. A grey square is a thing somebody owes you; this is a thing that was
// never asked for, and the two must not look alike.
const HeatCell = ({ cell }) => {
  if (!cell) return <td className="px-2 py-1.5" />;
  const {
    submitted, stale, in_scope: inScope, line_count: lines, global_line_count: globalLines,
    submitted_at: at, submitted_by: by, last_edit_at: edit,
  } = cell;

  if (!inScope) {
    return (
      <td className="px-1 py-1 text-center">
        <span
          className="text-slate-300 dark:text-slate-600 cursor-default select-none"
          title={submitted
            ? 'Not budgeted for this site in this version — yet a sign-off exists against it. Either the GL budget lines are missing or the sign-off is meaningless.'
            : 'Not budgeted. This version has no GL budget lines for this module at this site, so there is nothing to submit.'}
        >
          {submitted ? '!' : '·'}
        </span>
      </td>
    );
  }

  // How the square got onto the map, so a surprising one can be traced back to
  // the setup that put it there rather than argued about.
  const why = lines
    ? plural(lines, 'GL budget line', 'GL budget lines')
      + (globalLines ? `, plus ${plural(globalLines, 'line', 'lines')} set to all sites` : '')
    : `${plural(globalLines, 'GL budget line', 'GL budget lines')} set to all sites — this module is open at every site because of it`;

  const tone = stale
    ? 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-500/20 dark:text-amber-300 dark:border-amber-500/40'
    : submitted
      ? 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-300 dark:border-emerald-500/40'
      : 'bg-slate-100 text-slate-400 border-slate-200 dark:bg-slate-700/50 dark:text-slate-500 dark:border-slate-600';

  const state = stale
    ? `Submitted ${fmtFull(at)}${by ? ` by ${by}` : ''}\nEDITED SINCE: ${fmtFull(edit)}\nThe sign-off no longer covers these numbers.`
    : submitted
      ? `Submitted ${fmtFull(at)}${by ? ` by ${by}` : ''}`
      : edit ? `Not submitted. Last edited ${fmtFull(edit)}.` : 'Not submitted. Nothing entered yet.';
  const title = `${state}\n\nBudgeted: ${why}.`;

  return (
    <td className="px-1 py-1">
      <div className={`mx-auto w-full min-w-[74px] px-1.5 py-1.5 rounded-md border text-center cursor-default ${tone}`} title={title}>
        <span className="block text-[10px] font-bold leading-tight">
          {stale ? 'Edited' : submitted ? 'Signed' : '—'}
        </span>
        <span className="block text-[9px] opacity-80 leading-tight">
          {stale ? fmtWhen(edit) : submitted ? fmtWhen(at) : ''}
        </span>
      </div>
    </td>
  );
};

const Stat = ({ label, value, sub, tone }) => {
  const cls = tone === 'bad'
    ? 'border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10'
    : tone === 'warn'
      ? 'border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10'
      : tone === 'good'
        ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-500/40 dark:bg-emerald-500/10'
        : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800';
  return (
    <div className={`px-3 py-2 rounded-lg border ${cls}`}>
      <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
      <span className="text-sm font-bold text-slate-800 dark:text-slate-100">{value}</span>
      {sub && <span className="ml-1.5 text-[10px] text-slate-400">{sub}</span>}
    </div>
  );
};
